import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { CalendarApi } from "../../api/CalendarApi";
import type { CreateDatetimeRange } from "../../generated/dto/CreateDatetimeRange";
import type { CreateSunsetTime } from "../../generated/dto/CreateSunsetTime";
import type { CreateYearlyDate } from "../../generated/dto/CreateYearlyDate";
import type { CreateYearlyDateItem } from "../../generated/dto/CreateYearlyDateItem";
import type { DatetimeRangeStaff } from "../../generated/dto/DatetimeRangeStaff";
import type { DeleteDatetimeRangeStaff } from "../../generated/dto/DeleteDatetimeRangeStaff";
import type { DeleteSunsetTimeStaff } from "../../generated/dto/DeleteSunsetTimeStaff";
import type { DeleteYearlyDateItemStaff } from "../../generated/dto/DeleteYearlyDateItemStaff";
import type { DeleteYearlyDateStaff } from "../../generated/dto/DeleteYearlyDateStaff";
import type { SunsetTimeStaff } from "../../generated/dto/SunsetTimeStaff";
import type { UpdateDatetimeRange } from "../../generated/dto/UpdateDatetimeRange";
import type { UpdateSunsetTime } from "../../generated/dto/UpdateSunsetTime";
import type { UpdateYearlyDate } from "../../generated/dto/UpdateYearlyDate";
import type { UpdateYearlyDateItem } from "../../generated/dto/UpdateYearlyDateItem";
import type { YearlyDateFetchOptions } from "../../generated/dto/YearlyDateFetchOptions";
import type { YearlyDateItemStaff } from "../../generated/dto/YearlyDateItemStaff";
import type { YearlyDateStaff } from "../../generated/dto/YearlyDateStaff";

export class CalendarApiClient implements CalendarApi {
  constructor(private readonly client = useClient()) {}

  async getSunsetTimeStaff(id: number): Promise<SunsetTimeStaff> {
    const { data } = await this.client.get<unknown>(`/sunsettime/staff/${id}`);
    return deserializeCbiDto<SunsetTimeStaff>(data, "generated.dto.SunsetTimeStaff");
  }

  async getDatetimeRangeStaff(id: number): Promise<DatetimeRangeStaff> {
    const { data } = await this.client.get<unknown>(`/datetimerange/staff/${id}`);
    return deserializeCbiDto<DatetimeRangeStaff>(data, "generated.dto.DatetimeRangeStaff");
  }

  async getYearlyDateItemStaff(id: number): Promise<YearlyDateItemStaff> {
    const { data } = await this.client.get<unknown>(`/yearlydateitem/staff/${id}`);
    return deserializeCbiDto<YearlyDateItemStaff>(data, "generated.dto.YearlyDateItemStaff");
  }

  async getYearlyDateStaff(id: number, options?: YearlyDateFetchOptions): Promise<YearlyDateStaff> {
    const { data } = await this.client.get<unknown>(`/yearlydate/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.YearlyDateFetchOptions") });
    return deserializeCbiDto<YearlyDateStaff>(data, "generated.dto.YearlyDateStaff");
  }

  async createSunsetTimeStaff(source: CreateSunsetTime): Promise<SunsetTimeStaff> {
    const { data } = await this.client.post<unknown>(`/sunsettime/staff`, serializeCbiDto(source, "generated.dto.CreateSunsetTime"));
    return deserializeCbiDto<SunsetTimeStaff>(data, "generated.dto.SunsetTimeStaff");
  }

  async createDatetimeRangeStaff(source: CreateDatetimeRange): Promise<DatetimeRangeStaff> {
    const { data } = await this.client.post<unknown>(`/datetimerange/staff`, serializeCbiDto(source, "generated.dto.CreateDatetimeRange"));
    return deserializeCbiDto<DatetimeRangeStaff>(data, "generated.dto.DatetimeRangeStaff");
  }

  async createYearlyDateItemStaff(source: CreateYearlyDateItem): Promise<YearlyDateItemStaff> {
    const { data } = await this.client.post<unknown>(`/yearlydateitem/staff`, serializeCbiDto(source, "generated.dto.CreateYearlyDateItem"));
    return deserializeCbiDto<YearlyDateItemStaff>(data, "generated.dto.YearlyDateItemStaff");
  }

  async createYearlyDateStaff(source: CreateYearlyDate): Promise<YearlyDateStaff> {
    const { data } = await this.client.post<unknown>(`/yearlydate/staff`, serializeCbiDto(source, "generated.dto.CreateYearlyDate"));
    return deserializeCbiDto<YearlyDateStaff>(data, "generated.dto.YearlyDateStaff");
  }

  async updateSunsetTimeStaff(source: UpdateSunsetTime): Promise<SunsetTimeStaff> {
    const { data } = await this.client.put<unknown>(`/sunsettime/staff/${source.rowId}`, serializeCbiDto(source, "generated.dto.UpdateSunsetTime"));
    return deserializeCbiDto<SunsetTimeStaff>(data, "generated.dto.SunsetTimeStaff");
  }

  async updateDatetimeRangeStaff(source: UpdateDatetimeRange): Promise<DatetimeRangeStaff> {
    const { data } = await this.client.put<unknown>(`/datetimerange/staff/${source.rangeId}`, serializeCbiDto(source, "generated.dto.UpdateDatetimeRange"));
    return deserializeCbiDto<DatetimeRangeStaff>(data, "generated.dto.DatetimeRangeStaff");
  }

  async updateYearlyDateItemStaff(source: UpdateYearlyDateItem): Promise<YearlyDateItemStaff> {
    const { data } = await this.client.put<unknown>(`/yearlydateitem/staff/${source.itemId}`, serializeCbiDto(source, "generated.dto.UpdateYearlyDateItem"));
    return deserializeCbiDto<YearlyDateItemStaff>(data, "generated.dto.YearlyDateItemStaff");
  }

  async updateYearlyDateStaff(source: UpdateYearlyDate): Promise<YearlyDateStaff> {
    const { data } = await this.client.put<unknown>(`/yearlydate/staff/${source.dateId}`, serializeCbiDto(source, "generated.dto.UpdateYearlyDate"));
    return deserializeCbiDto<YearlyDateStaff>(data, "generated.dto.YearlyDateStaff");
  }

  async deleteSunsetTimeStaff(source: DeleteSunsetTimeStaff): Promise<void> {
    await this.client.delete(`/sunsettime/staff/${source.rowId}`);
  }

  async deleteDatetimeRangeStaff(source: DeleteDatetimeRangeStaff): Promise<void> {
    await this.client.delete(`/datetimerange/staff/${source.rangeId}`);
  }

  async deleteYearlyDateItemStaff(source: DeleteYearlyDateItemStaff): Promise<void> {
    await this.client.delete(`/yearlydateitem/staff/${source.itemId}`);
  }

  async deleteYearlyDateStaff(source: DeleteYearlyDateStaff): Promise<void> {
    await this.client.delete(`/yearlydate/staff/${source.dateId}`);
  }

  async getYearlyDateStaffItemId(id: number): Promise<YearlyDateItemStaff | null> {
    const { data } = await this.client.get<unknown>(`/yearlydate/staff/${id}/itemid`);
    return deserializeCbiDto<YearlyDateItemStaff | null>(data, "generated.dto.YearlyDateItemStaff");
  }
}

export const useCalendarApi = (): CalendarApi => new CalendarApiClient();
